import { useMemo, useState, type ReactNode } from 'react'

import {
  dayWritten,
  FILTER_LABELS,
  labelsIn,
  rangeRead,
  rangeWritten,
  withFilter,
  type FilterName,
  type LabelHeld,
} from '@gymcloud/set-search'

import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover'
import { FilterChip } from '~/components/ui/filter-chip'
import { SidewaysRow } from '~/components/ui/sideways-row'
import { useDataState } from '~/data/data-context'
import { cn } from '~/lib/cn'
import { readingHere } from '~/lib/set-search-here'

/**
 * The filters of a search of training history — the search spec, Filters. Each writes its own
 * labels into the search and takes them out again, so what the field says is the whole of the
 * search: a filter only ever reads the search it is handed and hands back another.
 */
export function SearchFilters({
  search,
  onSearch,
}: {
  /** The search as it stands. */
  search: string
  /** Searched at once, with what a filter made of it. */
  onSearch: (next: string) => void
}) {
  const reading = useMemo(() => readingHere(), [])
  const held = useMemo(() => labelsIn(search, reading), [search, reading])
  const write = (filter: FilterName, labels: readonly { label: string; value: string }[]) => {
    onSearch(withFilter(search, filter, labels, reading))
  }
  const ofFilter = (filter: FilterName) =>
    held.filter((label) => (FILTER_LABELS[filter] as readonly string[]).includes(label.label))

  return (
    <SidewaysRow label="Filters" role="group" className="gap-2 pb-1">
      <FilterMenu name="When" held={ofFilter('when')}>
        {(close) => (
          <WhenPicker
            dayFirst={reading.dayFirst}
            held={ofFilter('when')}
            onChoose={(labels) => {
              write('when', labels)
              close()
            }}
            onRange={(labels) => {
              write('when', labels)
            }}
          />
        )}
      </FilterMenu>
      {(['exercise', 'workout', 'template', 'artist'] as const).map((filter) => (
        <FilterMenu key={filter} name={NAMES[filter]} held={ofFilter(filter)}>
          {() => (
            <NamesPicker
              filter={filter}
              chosen={ofFilter(filter)
                .filter((label) => !label.negated)
                .map((label) => label.value)}
              onChoose={(values) => {
                write(
                  filter,
                  values.map((value) => ({ label: filter, value })),
                )
              }}
            />
          )}
        </FilterMenu>
      ))}
      {(['weight', 'reps'] as const).map((filter) => (
        <FilterMenu key={filter} name={NAMES[filter]} held={ofFilter(filter)}>
          {() => (
            <RangePicker
              name={NAMES[filter]}
              held={ofFilter(filter).find((label) => !label.negated)?.value ?? ''}
              onChoose={(value) => {
                write(filter, value === null ? [] : [{ label: filter, value }])
              }}
            />
          )}
        </FilterMenu>
      ))}
    </SidewaysRow>
  )
}

const NAMES: Readonly<Record<FilterName, string>> = {
  when: 'When',
  exercise: 'Exercise',
  workout: 'Workout',
  template: 'Template',
  artist: 'Artist',
  weight: 'Weight',
  reps: 'Reps',
}

/** What a label held says on its filter's chip: the value, and not before one negated. */
function heldSaid(label: LabelHeld): string {
  const value =
    label.label === 'after' || label.label === 'before'
      ? `${label.label} ${label.value}`
      : label.value
  return label.negated ? `not ${value}` : value
}

/**
 * One filter's chip and what it opens. The chip says what the search holds of the filter's
 * labels, whoever wrote them, and is lit while any of them narrows the search.
 */
function FilterMenu({
  name,
  held,
  children,
}: {
  name: string
  held: readonly LabelHeld[]
  children: (close: () => void) => ReactNode
}) {
  const [open, setOpen] = useState(false)
  const lit = held.some((label) => !label.negated)
  const said = held.length === 0 ? name : `${name}: ${held.map(heldSaid).join(', ')}`
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <FilterChip label={said} on={lit} opens />
      </PopoverTrigger>
      <PopoverContent align="start" aria-label={name} className="w-80 p-3">
        {children(() => {
          setOpen(false)
        })}
      </PopoverContent>
    </Popover>
  )
}

const menuRow =
  'flex w-full items-center rounded-xl px-3 py-2 text-left text-sm transition-colors hover:bg-hover'

/** The years sets were performed in, newest first, on this device's calendar. */
function useYears(): number[] {
  const { sets } = useDataState()
  return useMemo(
    () =>
      [...new Set(sets.map((set) => new Date(set.performedAt).getFullYear()))].sort(
        (a, b) => b - a,
      ),
    [sets],
  )
}

/** A day a label was given, as a date field holds one, or empty where it is not a whole day. */
function isoOf(value: string, dayFirst: boolean): string {
  const parts = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value)
  if (parts === null) return ''
  const [, first = '', second = '', year = ''] = parts
  const [day, month] = dayFirst ? [first, second] : [second, first]
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
}

/** A date field's day, as `after:` and `before:` are written with it. */
function writtenOf(iso: string, dayFirst: boolean): string {
  const [year = 0, month = 0, day = 0] = iso.split('-').map(Number)
  return dayWritten(year, month, day, dayFirst)
}

const field =
  'mt-1 w-full rounded-lg bg-surface-bright/60 px-2 py-1.5 text-sm text-ink outline-none'

/**
 * When a set was performed: a moment, a year, or a range of days. A moment or a year is chosen
 * and put away; a range writes as each end of it is set, so nothing set is ever lost to a press
 * that puts the filter away.
 */
function WhenPicker({
  dayFirst,
  held,
  onChoose,
  onRange,
}: {
  dayFirst: boolean
  held: readonly LabelHeld[]
  onChoose: (labels: { label: string; value: string }[]) => void
  onRange: (labels: { label: string; value: string }[]) => void
}) {
  const years = useYears()
  const edge = (label: 'after' | 'before') =>
    isoOf(held.find((each) => each.label === label && !each.negated)?.value ?? '', dayFirst)
  const [from, setFrom] = useState(() => edge('after'))
  const [to, setTo] = useState(() => edge('before'))
  const range = (nextFrom: string, nextTo: string) => {
    onRange([
      ...(nextFrom === '' ? [] : [{ label: 'after', value: writtenOf(nextFrom, dayFirst) }]),
      ...(nextTo === '' ? [] : [{ label: 'before', value: writtenOf(nextTo, dayFirst) }]),
    ])
  }
  const presets = [
    ['Today', 'today'],
    ['This week', 'this week'],
    ['Last week', 'last week'],
    ['This month', 'this month'],
    ['Last month', 'last month'],
  ] as const
  return (
    <div className="space-y-2">
      <div className="max-h-72 space-y-0.5 overflow-y-auto">
        <button
          type="button"
          className={menuRow}
          onClick={() => {
            onChoose([])
          }}
        >
          Any time
        </button>
        {presets.map(([said, value]) => (
          <button
            key={value}
            type="button"
            className={menuRow}
            onClick={() => {
              onChoose([{ label: 'on', value }])
            }}
          >
            {said}
          </button>
        ))}
        {years.map((year) => (
          <button
            key={year}
            type="button"
            className={menuRow}
            onClick={() => {
              onChoose([{ label: 'on', value: String(year) }])
            }}
          >
            {year}
          </button>
        ))}
      </div>
      <fieldset className="space-y-1 border-t border-white/10 pt-2">
        <legend className="px-1 pt-2 text-xs text-ink-muted">A range of days</legend>
        <div className="flex gap-2">
          <label className="flex-1 text-xs text-ink-muted">
            From
            <input
              type="date"
              value={from}
              onChange={(event) => {
                setFrom(event.target.value)
                range(event.target.value, to)
              }}
              className={field}
            />
          </label>
          <label className="flex-1 text-xs text-ink-muted">
            To
            <input
              type="date"
              value={to}
              onChange={(event) => {
                setTo(event.target.value)
                range(from, event.target.value)
              }}
              className={field}
            />
          </label>
        </div>
      </fieldset>
    </div>
  )
}

/**
 * Every name the history holds of one kind — exercises, workouts, templates, artists — the one
 * with the most sets first; several can be chosen at once, and each choice searches.
 */
function NamesPicker({
  filter,
  chosen,
  onChoose,
}: {
  filter: 'exercise' | 'workout' | 'template' | 'artist'
  chosen: readonly string[]
  onChoose: (values: string[]) => void
}) {
  const state = useDataState()
  const [typed, setTyped] = useState('')
  const names = useMemo(() => {
    const workouts = new Map(
      [...state.workouts, ...(state.currentWorkout === null ? [] : [state.currentWorkout])].map(
        (workout) => [workout.id, workout],
      ),
    )
    const runs = new Map(state.enrollments.map((run) => [run.id, run]))
    const counted = new Map<string, { name: string; count: number }>()
    const count = (name: string | null | undefined) => {
      if (name == null || name.trim() === '') return
      const key = name.trim().toLowerCase()
      const was = counted.get(key)
      counted.set(key, { name: was?.name ?? name.trim(), count: (was?.count ?? 0) + 1 })
    }
    for (const set of state.sets) {
      if (set.isWarmup) continue
      const workout = set.workoutId === null ? undefined : workouts.get(set.workoutId)
      if (filter === 'exercise') count(set.exerciseName)
      if (filter === 'workout') count(workout?.name)
      if (filter === 'template') {
        count(workout?.enrollmentId == null ? null : runs.get(workout.enrollmentId)?.templateName)
      }
      if (filter === 'artist') {
        for (const artist of new Set((set.songs ?? []).map((song) => song.artist))) count(artist)
      }
    }
    return [...counted.values()].sort((a, b) => b.count - a.count).map((each) => each.name)
  }, [state.sets, state.workouts, state.currentWorkout, state.enrollments, filter])
  const isChosen = (name: string) =>
    chosen.some((value) => value.toLowerCase() === name.toLowerCase())
  const shown = names.filter((name) => name.toLowerCase().includes(typed.trim().toLowerCase()))
  return (
    <div className="space-y-2">
      <input
        type="search"
        aria-label={`Find ${NAMES[filter].toLowerCase()}`}
        placeholder="Find"
        value={typed}
        onChange={(event) => {
          setTyped(event.target.value)
        }}
        className="w-full rounded-lg bg-surface-bright/60 px-3 py-1.5 text-sm outline-none"
      />
      {names.length === 0 ? (
        <p className="px-1 py-2 text-sm text-ink-muted">
          Nothing of this kind in your history yet.
        </p>
      ) : (
        <ul className="max-h-64 space-y-0.5 overflow-y-auto">
          {shown.map((name) => (
            <li key={name}>
              <label className={cn(menuRow, 'gap-3')}>
                <input
                  type="checkbox"
                  checked={isChosen(name)}
                  onChange={() => {
                    onChoose(
                      isChosen(name)
                        ? chosen.filter((value) => value.toLowerCase() !== name.toLowerCase())
                        : [...chosen, name],
                    )
                  }}
                  className="size-4 accent-[var(--color-accent)]"
                />
                <span className="truncate">{name}</span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * A lowest and a highest, either left empty: a range, that much or more, or that much or less.
 * It writes as it is typed, so there is nothing typed here the search does not already hold.
 */
function RangePicker({
  name,
  held,
  onChoose,
}: {
  name: string
  /** The value the search holds for this filter, which the fields start from. */
  held: string
  onChoose: (value: string | null) => void
}) {
  const [lowest, setLowest] = useState(() => rangeRead(held).lowest)
  const [highest, setHighest] = useState(() => rangeRead(held).highest)
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <label className="flex-1 text-xs text-ink-muted">
          Lowest
          <input
            type="number"
            inputMode="decimal"
            min={0}
            step="any"
            aria-label={`Lowest ${name.toLowerCase()}`}
            value={lowest}
            onChange={(event) => {
              setLowest(event.target.value)
              onChoose(rangeWritten(event.target.value, highest))
            }}
            className={field}
          />
        </label>
        <label className="flex-1 text-xs text-ink-muted">
          Highest
          <input
            type="number"
            inputMode="decimal"
            min={0}
            step="any"
            aria-label={`Highest ${name.toLowerCase()}`}
            value={highest}
            onChange={(event) => {
              setHighest(event.target.value)
              onChoose(rangeWritten(lowest, event.target.value))
            }}
            className={field}
          />
        </label>
      </div>
      <button
        type="button"
        disabled={lowest === '' && highest === ''}
        className="w-full rounded-full px-4 py-1.5 text-sm ring-1 ring-white/15 hover:bg-hover disabled:opacity-50"
        onClick={() => {
          setLowest('')
          setHighest('')
          onChoose(null)
        }}
      >
        Clear
      </button>
    </div>
  )
}
